import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatInteger } from '@/lib/language';
import { formatMicros, hasEvidenceSplit, type AdsFeedSummary, type AdsFeedSummaryV2 } from '@/lib/adsFeedSummary';

export interface AdsLeakListProps {
  summary: AdsFeedSummary;
  currency: string | null;
  open: boolean;
  onToggle: () => void;
  onOptimizeUnoptimized?: () => void;
  onShowDisapproved?: () => void;
  /**
   * Ein Optimierungslauf laeuft bereits (oder der Merchant-Zugriff ist
   * entzogen). Dieselbe Groesse, die die Baustellen-Knoepfe der Seite sperrt:
   * die Zeile "nie optimiert trotz Klicks" ruft denselben Lauf, und ein
   * zweiter Klick daneben startete ihn ein zweites Mal.
   */
  busy?: boolean;
}

/**
 * "Wo sich ein genauer Blick lohnt" (Spec 2026-09-23-kapitel3-evidenzschwelle-
 * design.md, D4/D5). Kein Wort "Nulldreher"/"Verlust" mehr -- Report Teil 3
 * zeigt, dass die frueher so benannten Produkte ueberwiegend wenige Klicks
 * hatten und in den vier Wochen danach Umsatz brachten. Die Zahl legte dem
 * Kunden nahe, Produkte abzuschalten, die in unseren eigenen Daten verkaufen.
 *
 * `schema_version 2` (v2) teilt Nullverkaeufer und Kostentreiber je an der
 * Evidenzschwelle (`evidence.min_clicks`) in "ab Schwelle" (bei der
 * Conversion-Rate des Kontos kaum noch Zufall, SOFERN `evidence.reliable`
 * das auch traegt -- Review-Fund I2, siehe `ZeroSellerRows`) und "unter
 * Schwelle" (zu wenig Klicks fuer ein Urteil, noch nicht entschieden).
 * `schema_version 1` (v1, Bestand vor dem naechsten naechtlichen Abruf) kennt
 * diese Trennung nicht -- dort steht ein neutraler Satz statt erfundener
 * Zahlen (D4: "keine erfundene Aufteilung").
 *
 * Die vier Positionen ueberlappen sich (Kostentreiber ist eine Teilmenge der
 * Nullverkaeufer, nie optimierte ueberschneiden beide, Disapproved rechnet
 * ueber 7 Tage). Deshalb NIRGENDS eine Summe -- eine addierte Zahl waere
 * groesser als der tatsaechliche Betrag und damit eine Falschaussage ueber
 * fremdes Geld.
 */
export function AdsLeakList({ summary, currency, open, onToggle, onOptimizeUnoptimized, onShowDisapproved, busy }: AdsLeakListProps) {
  const { t, i18n } = useTranslation('optimization');
  const lang = i18n?.language;
  const l = summary.leaks;
  const v2 = hasEvidenceSplit(summary) ? summary : null;
  const money = (micros: number) => formatMicros(micros, currency, lang);

  return (
    <div data-testid="ads-leaks" className="flex flex-col gap-2 border-t border-border/60 pt-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h3 className="text-sm font-heading font-bold">{t('feedCheck.ads.leaks.title')}</h3>
          <p className="text-sm text-muted-foreground">
            {/* Der Betrag steht nur, wenn die Schwelle ein Urteil traegt UND es
                Produkte ab Schwelle gibt (Final-Review I-1, M-2). Sonst zaehlt
                die Zeile nur, ohne "genug Klicks" zu behaupten. */}
            {v2 && (!v2.evidence.reliable || v2.leaks.zero_sellers.above.products === 0)
              ? t('feedCheck.ads.leaks.collapsedV2Count', {
                above: formatInteger(v2.leaks.zero_sellers.above.products, lang),
                min: formatInteger(v2.evidence.min_clicks, lang),
                below: formatInteger(v2.leaks.zero_sellers.below.products, lang),
                unopt: formatInteger(l.unoptimized_with_clicks.products, lang),
                disapproved: formatInteger(l.disapproved.products, lang),
              })
              : v2
              ? t('feedCheck.ads.leaks.collapsedV2', {
                aboveCost: money(v2.leaks.zero_sellers.above.cost_micros),
                below: formatInteger(v2.leaks.zero_sellers.below.products, lang),
                unopt: formatInteger(l.unoptimized_with_clicks.products, lang),
                disapproved: formatInteger(l.disapproved.products, lang),
              })
              : t('feedCheck.ads.leaks.collapsedV1', {
                unopt: formatInteger(l.unoptimized_with_clicks.products, lang),
                disapproved: formatInteger(l.disapproved.products, lang),
              })}
          </p>
        </div>
        <button
          type="button"
          className="shrink-0 text-sm font-medium text-primary hover:underline print:hidden"
          aria-expanded={open}
          onClick={onToggle}
        >
          {open ? t('feedCheck.ads.leaks.close') : t('feedCheck.ads.leaks.open')}
        </button>
      </div>

      {open && (
        <div className="flex flex-col gap-0.5">
          {v2 ? (
            <ZeroSellerRows summary={v2} money={money} lang={lang} />
          ) : (
            <p data-testid="ads-leak-v1-pending" className="py-2 text-sm text-muted-foreground">
              {t('feedCheck.ads.leaks.v1Pending')}
            </p>
          )}
          <LeakRow
            testId="ads-leak-row-unopt"
            amount={money(l.unoptimized_with_clicks.cost_micros)}
            tone="text-warning-ink"
            finding={t('feedCheck.ads.leaks.unopt', { count: l.unoptimized_with_clicks.products, formatted: formatInteger(l.unoptimized_with_clicks.products, lang) })}
            why={t('feedCheck.ads.leaks.unoptWhy', {
              count: l.unoptimized_with_clicks.clicks,
              clicks: formatInteger(l.unoptimized_with_clicks.clicks, lang),
            })}
            action={onOptimizeUnoptimized && l.unoptimized_with_clicks.products > 0
              ? { label: t('feedCheck.ads.leaks.unoptCta', { count: l.unoptimized_with_clicks.products, formatted: formatInteger(l.unoptimized_with_clicks.products, lang) }), onClick: onOptimizeUnoptimized, disabled: busy }
              : undefined}
          />
          <LeakRow
            testId="ads-leak-row-disapproved"
            // Ohne Kosten in den letzten 7 Tagen gibt es keinen Betrag, den man
            // zeigen koennte. Dann steht die Stueckzahl da, nie eine 0 als
            // Geldbetrag -- die laese sich als "kostet nichts, also egal".
            amount={l.disapproved.with_cost_7d > 0 ? money(l.disapproved.cost_micros_7d) : formatInteger(l.disapproved.products, lang)}
            tone="text-warning-ink"
            finding={t('feedCheck.ads.leaks.disapproved', { count: l.disapproved.products, formatted: formatInteger(l.disapproved.products, lang) })}
            why={l.disapproved.with_cost_7d > 0
              ? t('feedCheck.ads.leaks.disapprovedWhyCost', {
                withCost: formatInteger(l.disapproved.with_cost_7d, lang),
                cost: money(l.disapproved.cost_micros_7d),
              })
              : t('feedCheck.ads.leaks.disapprovedWhyNoCost')}
            action={onShowDisapproved && l.disapproved.products > 0
              ? { label: t('feedCheck.ads.leaks.disapprovedCta'), onClick: onShowDisapproved, disabled: busy }
              : undefined}
          />
          <p className="pt-1 text-[11px] text-muted-foreground">{t('feedCheck.ads.leaks.noSum')}</p>
        </div>
      )}
    </div>
  );
}

/**
 * Die drei v2-Zeilen: "ab Schwelle" (mit Top-3-Liste), direkt danach der
 * Kostentreiber (eine Teilmenge der Nullverkaeufer, nicht nur der Nullverkaeufer
 * ab Schwelle -- `drain_no_sale` hat selbst ein `below`), zuletzt "unter
 * Schwelle". Eigene Komponente statt Inline-JSX in `AdsLeakList`, weil sie
 * ihre eigene Grenzfall-Logik traegt (leere Schwellen-Seite, Zeile weglassen)
 * und diese Logik sonst den Hauptkoerper unlesbar macht.
 *
 * Reihenfolge ab Schwelle -> Kostentreiber -> unter Schwelle ist Absicht
 * (Review-Fund I1): "davon" in der Kostentreiber-Zeile zeigt auf die davor
 * stehende Menge. Stuende die Zeile hinter "unter Schwelle" (wie zuerst
 * gebaut), zeigte "davon" auf die FALSCHE Menge -- eine Teilmenge der
 * unentschiedenen Produkte, obwohl der Kostentreiber eine Teilmenge der Menge
 * AB Schwelle ist.
 */
function ZeroSellerRows({ summary, money, lang }: {
  summary: AdsFeedSummaryV2;
  money: (micros: number) => string;
  lang: string | undefined;
}) {
  const { t } = useTranslation('optimization');
  const minClicks = summary.evidence.min_clicks;
  const above = summary.leaks.zero_sellers.above;
  const below = summary.leaks.zero_sellers.below;
  const drain = summary.leaks.drain_no_sale;
  // Haelt die Schwelle wirklich das Versprechen "kaum Zufall" (Review-Fund
  // I2)? `min_clicks` ist auf [20, 250] gekappt, und bei sehr kleiner oder
  // nicht bestimmbarer Conversion-Rate (z. B. Klicks ohne eine einzige
  // Conversion -- kaputtes Tracking eingeschlossen) haelt die Kappung selbst
  // nicht mehr. Die EINE Quelle fuer diese Frage sitzt im Schreiber
  // (`evidenceIsReliable`, `supabase/functions/_shared/ads-feed-summary.ts`);
  // hier wird nur gelesen, nie neu gerechnet.
  const reliable = summary.evidence.reliable;
  // Beide Seiten der Schwelle sind 0: nichts, worueber sich reden liesse.
  const showDrainRow = drain.above.products > 0 || drain.below.products > 0;

  return (
    <>
      {above.products > 0 ? (
        <LeakRow
          testId="ads-leak-row-zero-above"
          amount={money(above.cost_micros)}
          tone="text-warning-ink"
          finding={t('feedCheck.ads.leaks.zeroAbove', { count: above.products, formatted: formatInteger(above.products, lang), min: minClicks })}
          why={t(reliable ? 'feedCheck.ads.leaks.zeroAboveWhy' : 'feedCheck.ads.leaks.zeroAboveWhyUnreliable')}
        />
      ) : (
        <LeakRow
          testId="ads-leak-row-zero-above"
          amount={money(above.cost_micros)}
          // Null Produkte ab Schwelle: kein Warnton fuer "0 CHF" (E2E 23.09.2026).
          tone="text-muted-foreground"
          finding={t('feedCheck.ads.leaks.zeroAboveNone', { min: minClicks })}
        />
      )}
      {above.top.length > 0 && (
        <div className="mb-1.5 ml-0 flex flex-col border-l-2 border-muted pl-3 sm:ml-32">
          {above.top.slice(0, 3).map((p) => (
            <div key={p.product_id} className="flex gap-3 py-1 text-xs">
              <span className="min-w-0 flex-1 truncate">{p.title ?? p.product_id}</span>
              <span className="w-16 text-right text-muted-foreground tabular-nums">{t('feedCheck.ads.leaks.clicks', { count: p.clicks, formatted: formatInteger(p.clicks, lang) })}</span>
              <span className="w-20 text-right tabular-nums">{money(p.cost_micros)}</span>
            </div>
          ))}
          {above.products > 3 && (
            <div className="py-1 text-xs text-muted-foreground">
              {t('feedCheck.ads.leaks.aboveMore', { count: above.products - 3, formatted: formatInteger(above.products - 3, lang) })}
            </div>
          )}
        </div>
      )}
      {showDrainRow && (
        drain.above.products > 0 ? (
          <LeakRow
            testId="ads-leak-row-drain"
            amount={money(drain.above.cost_micros)}
            // I1: Ton auf warning statt destructive. Der Kostentreiber ist
            // eine Teilmenge der Ab-Schwelle-Zeile; eine Teilmenge in
            // schaerferem Rot als ihre Obermenge legte eine haertere Handlung
            // nahe, als der Text traegt.
            tone="text-warning-ink"
            finding={t('feedCheck.ads.leaks.drainV2', { count: drain.above.products, formatted: formatInteger(drain.above.products, lang), min: minClicks })}
            // M5: "Dazu kommen 0 mit weniger Klicks" waere Unsinn, wenn es
            // gar nichts darunter gibt.
            why={drain.below.products > 0
              ? t('feedCheck.ads.leaks.drainV2Why', {
                count: drain.below.products,
                below: formatInteger(drain.below.products, lang),
                cost: money(drain.below.cost_micros),
              })
              : undefined}
          />
        ) : (
          // M5: above=0, below>0 (sonst waere showDrainRow false). Kein
          // Betrag in Rot fuer null Produkte, kein "0 davon liegen ..." --
          // ein neutraler Satz wie bei zeroAboveNone.
          <LeakRow
            testId="ads-leak-row-drain"
            amount={money(drain.above.cost_micros)}
            tone="text-muted-foreground"
            finding={t('feedCheck.ads.leaks.drainAboveNone', { min: minClicks })}
            // E2E 23.09.2026: ohne diese Zeile verschwanden die Produkte des
            // Segments unter der Schwelle samt Kosten aus der Kostentreiber-Zeile.
            why={t('feedCheck.ads.leaks.drainV2Why', {
              count: drain.below.products,
              below: formatInteger(drain.below.products, lang),
              cost: money(drain.below.cost_micros),
            })}
          />
        )
      )}
      <LeakRow
        testId="ads-leak-row-zero-below"
        amount={money(below.cost_micros)}
        tone="text-muted-foreground"
        finding={t('feedCheck.ads.leaks.zeroBelow', { count: below.products, formatted: formatInteger(below.products, lang), min: minClicks })}
        why={t('feedCheck.ads.leaks.zeroBelowWhy')}
      />
    </>
  );
}

function LeakRow({ testId, amount, tone, finding, why, action }: {
  testId: string;
  amount: string;
  tone: string;
  finding: string;
  /** Fehlt, wenn `finding` allein schon der ganze Satz ist (z. B. `zeroAboveNone`). */
  why?: string;
  action?: { label: string; onClick: () => void; disabled?: boolean };
}) {
  return (
    // Unter `sm` (390 px) stapeln sich Betrag, Text und Aktion statt
    // horizontal ueberzulaufen -- die festen Breiten `w-24`/`w-40` gelten erst
    // ab `sm:`, wo der Rumpf breit genug fuer die alte einzeilige Anordnung
    // ist. Keine visuelle Aenderung ab `sm:`, nur unterhalb.
    <div data-testid={testId} className="flex flex-col gap-1.5 py-2 sm:flex-row sm:items-start sm:gap-3">
      <div className={`font-heading text-xl font-bold tabular-nums ${tone} sm:min-w-32 sm:shrink-0 sm:whitespace-nowrap`}>{amount}</div>
      <div className="min-w-0 flex-1 text-sm">
        {finding}
        {why !== undefined && <span className="text-muted-foreground"> · {why}</span>}
      </div>
      <div className="sm:w-40 sm:shrink-0 sm:text-right">
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            disabled={action.disabled}
            className="text-xs font-bold text-primary hover:underline disabled:pointer-events-none disabled:opacity-50 print:hidden"
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}
